import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Timer, Users, ChatCircleText, ArrowUpRight } from "@phosphor-icons/react";
import {
  selectClinicId,
  selectIsTrialPlan,
  selectShouldShowPaywall,
  selectTherapistName,
  selectTrialDaysRemaining,
} from "../../store/slices/authSlice";
import { getClinicUsageData, getEffectiveLimits } from "../../services/planService";
import TrialContactModal from "./TrialContactModal";
import "./TrialUsageBanner.css";

const URGENT_USAGE_RATIO = 0.8;
const URGENT_DAYS_REMAINING = 10;

/**
 * Compact persistent bar shown under the dashboard header for trial clinics:
 * days remaining + patients/SMS usage meters against the trial limits.
 * Turns urgent (warm styling) near the end of the trial or near a limit,
 * and its CTA opens the existing TrialContactModal (lead capture).
 */
function TrialUsageBanner() {
  const isTrialPlan = useSelector(selectIsTrialPlan);
  const shouldShowPaywall = useSelector(selectShouldShowPaywall);
  const daysRemaining = useSelector(selectTrialDaysRemaining);
  const clinicId = useSelector(selectClinicId);
  const therapistName = useSelector(selectTherapistName);

  const [usage, setUsage] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    if (!clinicId || !isTrialPlan) return;
    let cancelled = false;
    getClinicUsageData(clinicId)
      .then((data) => {
        if (!cancelled && data) setUsage(data);
      })
      .catch((error) => {
        console.error("[CK-5040] Error loading trial usage for banner:", {
          errorCode: "CK-5040",
          errorMessage: error.message,
          timestamp: new Date().toISOString(),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, isTrialPlan]);

  // Expired trials are handled by the blocking paywall popup
  if (!isTrialPlan || shouldShowPaywall) return null;

  // Effective trial limits honour NiV per-clinic overrides (falls back to plan defaults)
  const { maxPatients, smsPerMonth } = getEffectiveLimits("trial", usage);
  const patientCount = usage?.patientCount ?? null;
  const smsUsed = usage?.smsUsedThisMonth ?? null;

  const patientRatio = patientCount !== null ? patientCount / maxPatients : 0;
  const smsRatio = smsUsed !== null ? smsUsed / smsPerMonth : 0;
  const isUrgent =
    (daysRemaining !== null && daysRemaining <= URGENT_DAYS_REMAINING) ||
    patientRatio >= URGENT_USAGE_RATIO ||
    smsRatio >= URGENT_USAGE_RATIO;

  const meterClass = (ratio) =>
    ratio >= 1 ? "is-full" : ratio >= URGENT_USAGE_RATIO ? "is-near" : "";

  return (
    <>
      <div className={`trial-usage-banner ${isUrgent ? "is-urgent" : ""}`}>
        <div className="trial-usage-days">
          <Timer size={16} weight="fill" />
          <span>
            תקופת ניסיון
            {daysRemaining !== null && (
              <>
                {" — "}
                <strong>
                  {daysRemaining === 1 ? "נותר יום אחד" : `נותרו ${daysRemaining} ימים`}
                </strong>
              </>
            )}
          </span>
        </div>

        {patientCount !== null && (
          <div className={`trial-usage-meter ${meterClass(patientRatio)}`}>
            <Users size={15} weight="regular" />
            <span className="trial-usage-meter-label">מטופלים</span>
            <span className="trial-usage-meter-count">
              {patientCount}/{maxPatients}
            </span>
            <span className="trial-usage-meter-track">
              <span
                className="trial-usage-meter-fill"
                style={{ width: `${Math.min(patientRatio * 100, 100)}%` }}
              />
            </span>
          </div>
        )}

        {smsUsed !== null && (
          <div className={`trial-usage-meter ${meterClass(smsRatio)}`}>
            <ChatCircleText size={15} weight="regular" />
            <span className="trial-usage-meter-label">SMS החודש</span>
            <span className="trial-usage-meter-count">
              {smsUsed}/{smsPerMonth}
            </span>
            <span className="trial-usage-meter-track">
              <span
                className="trial-usage-meter-fill"
                style={{ width: `${Math.min(smsRatio * 100, 100)}%` }}
              />
            </span>
          </div>
        )}

        <button
          type="button"
          className="trial-usage-cta"
          onClick={() => setShowContactModal(true)}
        >
          <span>המשך עם CliniKit</span>
          <ArrowUpRight size={14} weight="bold" />
        </button>
      </div>

      {showContactModal && (
        <TrialContactModal
          variant="warning"
          daysRemaining={daysRemaining ?? 0}
          therapistName={therapistName}
          onAcknowledge={() => setShowContactModal(false)}
        />
      )}
    </>
  );
}

export default TrialUsageBanner;
